import React, { useCallback, useEffect, useRef, useState } from "react";
import { cleanCoCoName } from "../helpers/cocoNameHelper";
import {
  FaChevronLeft,
  FaChevronRight,
  FaCog,
  FaFilter,
  FaSearch,
  FaUsers,
} from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import { useNotifications } from "../context/NotificationsContext";
import { useTranslation } from "../context/TranslationContext";
import { fetchCoordinatorDashboard } from "../helpers/apiHelper";
import { canUseCoordinatorGlobalSearch } from "../helpers/globalSearchHelper";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import "../styles/Views.css";
import CoordinatorAdvancedSearch from "./CoordinatorAdvancedSearch";

const DEFAULT_COLUMN_WIDTHS = {
  cocoName: 760,
  totalClients: 220,
};

const CoordinatorDashboard = ({ portalSelector }) => {
  const { tokens, user } = useAuth();
  const { features } = useFeatureFlags();
  const showAdvancedSearch = canUseCoordinatorGlobalSearch(features, user);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cocos, setCocos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const [continuationToken, setContinuationToken] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [section, setSection] = useState("");
  const [pageHistory, setPageHistory] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [isMobileTableLayout, setIsMobileTableLayout] = useState(false);
  const resizeStateRef = useRef(null);
  const dashboardRequestIdRef = useRef(0);

  const applyPage = useCallback((data, requestedToken = null) => {
    const nextPage = {
      cocos: Array.isArray(data?.cocos) ? data.cocos : [],
      continuationToken: data?.continuationToken || null,
      hasNextPage: Boolean(data?.hasNextPage),
      section: data?.section || "",
      requestedToken,
    };

    setCocos(nextPage.cocos);
    setContinuationToken(nextPage.continuationToken);
    setHasNextPage(nextPage.hasNextPage);
    setSection(nextPage.section);
    return nextPage;
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!tokens?.idToken) return;

    const requestId = ++dashboardRequestIdRef.current;
    setLoading(true);
    setError("");
    try {
      const data = await fetchCoordinatorDashboard(tokens.idToken, null, debouncedSearchTerm);
      if (requestId !== dashboardRequestIdRef.current) return;
      const firstPage = applyPage(data);
      setPageHistory([firstPage]);
      setPageIndex(0);
    } catch (requestError) {
      if (requestId !== dashboardRequestIdRef.current) return;
      console.error("Failed to fetch coordinator dashboard:", requestError);
      setError(t("coordinatorDashboard.loadError"));
    } finally {
      if (requestId === dashboardRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [tokens?.idToken, t, applyPage, debouncedSearchTerm]);

  const { refresh: refreshNotifications } = useNotifications();
  const handleRefresh = () => {
    if (loading) return;
    refreshNotifications();
    loadDashboard();
  };

  const loadNextPage = async () => {
    if (!tokens?.idToken || !continuationToken || loadingMore) return;

    const cachedPage = pageHistory[pageIndex + 1];
    if (cachedPage) {
      setCocos(cachedPage.cocos);
      setContinuationToken(cachedPage.continuationToken);
      setHasNextPage(cachedPage.hasNextPage);
      setSection(cachedPage.section);
      setPageIndex((current) => current + 1);
      return;
    }

    const requestId = ++dashboardRequestIdRef.current;
    setLoadingMore(true);
    setError("");
    try {
      const requestedToken = continuationToken;
      const data = await fetchCoordinatorDashboard(tokens.idToken, requestedToken, debouncedSearchTerm);
      if (requestId !== dashboardRequestIdRef.current) return;
      const nextPage = applyPage(data, requestedToken);
      setPageHistory((current) => [...current.slice(0, pageIndex + 1), nextPage]);
      setPageIndex((current) => current + 1);
    } catch (requestError) {
      if (requestId !== dashboardRequestIdRef.current) return;
      console.error("Failed to fetch coordinator dashboard page:", requestError);
      setError(t("coordinatorDashboard.loadError"));
    } finally {
      if (requestId === dashboardRequestIdRef.current) {
        setLoadingMore(false);
      }
    }
  };

  const loadPreviousPage = () => {
    const previousPage = pageHistory[pageIndex - 1];
    if (!previousPage || loading || loadingMore) return;

    setCocos(previousPage.cocos);
    setContinuationToken(previousPage.continuationToken);
    setHasNextPage(previousPage.hasNextPage);
    setSection(previousPage.section);
    setPageIndex((current) => Math.max(current - 1, 0));
  };

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const updateIsMobileTableLayout = (event) => {
      setIsMobileTableLayout(event.matches);
    };

    updateIsMobileTableLayout(mediaQuery);
    mediaQuery.addEventListener("change", updateIsMobileTableLayout);

    return () => {
      mediaQuery.removeEventListener("change", updateIsMobileTableLayout);
    };
  }, []);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!resizeStateRef.current) return;
      const { startX, startWidths } = resizeStateRef.current;
      const delta = event.clientX - startX;
      setColumnWidths({
        cocoName: Math.max(320, startWidths.cocoName + delta),
        totalClients: Math.max(160, startWidths.totalClients),
      });
    };

    const handlePointerUp = () => {
      resizeStateRef.current = null;
      document.body.classList.remove("is-resizing-table");
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      document.body.classList.remove("is-resizing-table");
    };
  }, []);

  const openCoCoDetails = (cocoId) => {
    if (cocoId) {
      const coco = cocos.find((item) => item.cocoId === cocoId);
      navigate(`/coordinator/${encodeURIComponent(cocoId)}`, {
        state: {
          cocoName: coco?.cocoName,
          totalClients: coco?.totalClients,
        },
      });
    }
  };

  const startColumnResize = (event) => {
    event.preventDefault();
    resizeStateRef.current = {
      startX: event.clientX,
      startWidths: columnWidths,
    };
    document.body.classList.add("is-resizing-table");
  };

  const tableWidth = columnWidths.cocoName + columnWidths.totalClients;

  return (
    <div className={`view-container${showAdvancedSearch ? " has-advanced-search" : ""}`}>
      {showAdvancedSearch && <CoordinatorAdvancedSearch />}
      <header className="clients-page-header">
        <div className="clients-title-group">
          {portalSelector}
          <h2>{t("coordinatorDashboard.title")}</h2>
        </div>
        <div className="clients-total-card" aria-live="polite">
          <span className="clients-total-icon"><FaUsers aria-hidden="true" /></span>
          <span className="clients-total-copy">
            <strong>{cocos.length}</strong>
            <small>{t("coordinatorDashboard.cocos")}</small>
          </span>
        </div>
      </header>

      <div className="filter-container coordinator-filter-container">
        <div className="filter-heading">
          <span className="filter-heading-icon"><FaFilter aria-hidden="true" /></span>
          <p className="note">{t("filters")}</p>
        </div>
        <div className="coordinator-filters">
          <div className="filter-item-container search-filter">
            <label className="sr-only" htmlFor="coordinator-search">
              {t("coordinatorDashboard.search")}
            </label>
            <FaSearch className="search-input-icon" aria-hidden="true" />
            <input
              id="coordinator-search"
              className="cpf-filter-input"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t("coordinatorDashboard.search")}
            />
          </div>
        </div>
        <div className="page-selector">
          <button
            type="button"
            className={`table-refresh-button${loading ? " is-loading" : ""}`}
            onClick={handleRefresh}
            disabled={loading}
            aria-label={t("clientsTable.refresh")}
            title={t("clientsTable.refresh")}
          >
            <FiRefreshCw aria-hidden="true" />
          </button>
        </div>
      </div>

      {section && (
        <div className="coordinator-page-note">
          <p className="note">
            {section === "TOP_50"
              ? t("coordinatorDashboard.top50Note")
              : t("coordinatorDashboard.alphabeticalNote")}
          </p>
          <span>{t("clientsTable.page")} {pageIndex + 1}</span>
        </div>
      )}

      {error && (
        <div className="dashboard-error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={loadDashboard}>{t("coordinatorDashboard.retry")}</button>
        </div>
      )}

      <div className="client-table-shell coordinator-table-shell">
        <div className="client-table-scroll">
          <table
            className="client-table coordinator-table"
            style={isMobileTableLayout ? undefined : { minWidth: `${tableWidth}px` }}
          >
            {!isMobileTableLayout && (
              <colgroup>
                <col style={{ width: `${columnWidths.cocoName}px` }} />
                <col style={{ width: `${columnWidths.totalClients}px` }} />
              </colgroup>
            )}
            <thead>
              <tr>
                <th scope="col">
                  <div className="resizable-header-content">
                    <span>{t("coordinatorDashboard.columns.cocoName")}</span>
                    <button
                      type="button"
                      className="column-resize-handle"
                      aria-label="Resize CoCo column"
                      onPointerDown={startColumnResize}
                    />
                  </div>
                </th>
                <th scope="col" className="numeric-column">{t("coordinatorDashboard.columns.totalClients")}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && !error && cocos.length > 0 ? (
                cocos.map((coco) => (
                  <tr
                    key={coco.cocoId}
                    className="clickable-table-row"
                    tabIndex={0}
                    onClick={() => openCoCoDetails(coco.cocoId)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openCoCoDetails(coco.cocoId);
                      }
                    }}
                  >
                    <td data-label={t("coordinatorDashboard.columns.cocoName")} className="client-primary-link">{cleanCoCoName(coco.cocoName) || "-"}</td>
                    <td data-label={t("coordinatorDashboard.columns.totalClients")} className="numeric-column">{coco.totalClients}</td>
                  </tr>
                ))
              ) : !loading && !error ? (
                <tr><td colSpan="2" className="empty-table-cell">{t("coordinatorDashboard.noResults")}</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {loading && <div className="loading-row"><FaCog className="spinner-cog" /></div>}
      </div>

      {!loading && !error && (pageIndex > 0 || hasNextPage) && (
        <div className="pagination-container">
          <button
            type="button"
            className="pagination-button"
            onClick={loadPreviousPage}
            disabled={loadingMore || pageIndex === 0}
          >
            <FaChevronLeft aria-hidden="true" />
            <span>{t("clientsTable.previous")}</span>
          </button>
          <span className="pagination-page-label">{t("clientsTable.page")} {pageIndex + 1}</span>
          <button
            type="button"
            className="pagination-button"
            onClick={loadNextPage}
            disabled={loadingMore || !hasNextPage}
          >
            <span>{loadingMore ? t("loading") : t("clientsTable.next")}</span>
            {loadingMore ? <FaCog className="spinner-cog" aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
          </button>
        </div>
      )}
    </div>
  );
};

export default CoordinatorDashboard;
